import { useMemo } from 'react';

import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import {
	ReferenceType,
	type Hco,
	type Hcp
} from '@/core/graphql/generated/types';

import { useGetAllPicklistsQuery } from '../_graphql/query/GetAllPicklists.hook';
import { extractCountriesAndStates } from '../_utils/extractCountriesAndStates';
import { isReferenceResults } from '../_utils/isReferenceResult';

type ReferenceOptions = {
	-readonly [
		K in keyof typeof ReferenceType as `${Uncapitalize<K>}Options`
	]: Array<Pick<DropdownItemBase, 'id' | 'name'>>;
};

interface PicklistReturn extends ReferenceOptions {
	yesOrNowOptions: { id: string; name: string }[];
	countryOptions: DropdownItemBase[];
	stateOptionsDictionary: Record<string, DropdownItemBase[]>;
}

type Props = {
	isModalOpen: boolean;
	countryId: Hcp['countryId'] | Hco['countryId'];
};

const getLowerBound = (name: string): number => {
	const cleanName = name.replace(/,/g, '');
	const match = cleanName.match(/^(\d+)/);

	return match ? parseInt(match[1], 10) : 0;
};

export const usePicklistValues = ({ isModalOpen, countryId }: Props) => {
	// Validation imposed by the API
	const hasValidCountry = countryId && countryId.length >= 2;

	const { data, loading } = useGetAllPicklistsQuery({
		skip: !isModalOpen || !countryId,
		variables: hasValidCountry ? { countryId } : undefined
	});

	const { countryOptions, stateOptionsDictionary } = useMemo(() => {
		return extractCountriesAndStates(data?.countries);
	}, [data?.countries]);

	const picklists = useMemo((): PicklistReturn => {
		const extractDataByKey = (
			key: ReferenceType
		): Array<Pick<DropdownItemBase, 'id' | 'name'>> => {
			const dataByKey = data?.[key];
			return isReferenceResults(dataByKey) ? dataByKey.results : [];
		};

		const dynamicOptions = {} as ReferenceOptions;

		// Dynamically build picklist objects from types
		(Object.keys(ReferenceType) as Array<keyof typeof ReferenceType>).forEach(
			key => {
				const propertyName =
					`${key.charAt(0).toLowerCase()}${key.slice(1)}Options` as keyof ReferenceOptions;
				dynamicOptions[propertyName] = extractDataByKey(ReferenceType[key]);
			}
		);

		// Note (Ben): Temporarily overriding order of count picklist
		dynamicOptions.countRangeOptions = [
			...dynamicOptions.countRangeOptions
		].sort((a, b) => getLowerBound(a.name) - getLowerBound(b.name));

		return {
			...dynamicOptions,
			yesOrNowOptions: [
				{ id: 'yes', name: 'Yes' },
				{ id: 'no', name: 'No' }
			],
			countryOptions,
			stateOptionsDictionary
		};
	}, [countryOptions, data, stateOptionsDictionary]);

	return {
		picklists,
		loading
	};
};
